import React, { useEffect, useState } from 'react';
import { C } from '../../utils/styles';
import Icon from '../Icon';
import { verifyCertificatePublic } from './learnerApi';
import { CertificateDocument } from './CertificateView';

// ----------------------------------------------------------------------------
// PublicVerifyCertificatePage — NO authentication required.
// Reached via the QR code on every certificate. Renders the same cert document
// with a "Verified" stamp if it's valid, or an "Invalid / Revoked" banner if not.
// ----------------------------------------------------------------------------

export default function PublicVerifyCertificatePage({ certificateId }) {
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    verifyCertificatePublic(certificateId)
      .then((d) => { if (mounted) setCert(d); })
      .catch((err) => { if (mounted) setError(err.message || 'Certificate not found'); })
      .finally(() => { if (mounted) setLoading(false); });
  }, [certificateId]);

  return (
    <div
      data-testid="public-verify-page"
      style={{
        minHeight: '100vh',
        background: '#f1f5f9',
        padding: '36px 16px',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700;800&family=Playfair+Display:ital,wght@1,500&display=swap" rel="stylesheet" />
      <div style={{ maxWidth: 980, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src="https://customer-assets.emergentagent.com/job_crazy-ellis-3/artifacts/hydsmihy_HealthCues_Workplace_Health_Solutions_Logo_simple.svg" alt="HealthCues" style={{ height: 48 }} />
          <h1 style={{ fontSize: 24, fontWeight: 800, color: C.text, margin: '14px 0 4px', letterSpacing: '-0.01em' }}>Certificate Verification</h1>
          <p style={{ color: C.textMid, fontSize: 14, margin: 0 }}>
            HealthCues maintains a public record of every certificate it issues.
          </p>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', color: C.textMid, fontStyle: 'italic' }}>Looking up certificate {certificateId}…</div>
        )}

        {!loading && (error || !cert) && (
          <div data-testid="verify-not-found" style={{ background: '#fff', padding: 32, borderRadius: 14, border: `1px solid ${C.red}33`, textAlign: 'center' }}>
            <Icon name="close-circle-outline" style={{ fontSize: 56, color: C.red }} />
            <h2 style={{ fontSize: 22, fontWeight: 800, color: C.red, margin: '12px 0 4px' }}>Certificate not found</h2>
            <p style={{ color: C.textMid, fontSize: 14, margin: 0 }}>
              We couldn't find a HealthCues certificate with the ID <code>{certificateId}</code>.
              Please check the ID and try again, or contact us if you believe this is in error.
            </p>
          </div>
        )}

        {!loading && cert && !cert.valid && (
          <div data-testid="verify-revoked" style={{ background: '#fff', padding: 32, borderRadius: 14, border: `1px solid ${C.red}33`, textAlign: 'center', marginBottom: 16 }}>
            <Icon name="alert-circle-outline" style={{ fontSize: 56, color: C.red }} />
            <h2 style={{ fontSize: 22, fontWeight: 800, color: C.red, margin: '12px 0 4px' }}>Certificate has been revoked</h2>
            <p style={{ color: C.textMid, fontSize: 14, margin: 0 }}>
              {cert.reason || 'This certificate is no longer valid.'}
            </p>
          </div>
        )}

        {!loading && cert && cert.valid && (
          <>
            <div data-testid="verify-valid" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: C.greenLight, border: `1px solid ${C.greenBorder}`, borderRadius: 10, marginBottom: 16 }}>
              <Icon name="shield-checkmark" style={{ fontSize: 24, color: C.green }} />
              <div>
                <div style={{ fontWeight: 800, color: C.green, fontSize: 14 }}>This certificate is valid</div>
                <div style={{ fontSize: 12, color: C.textMid }}>Issued by HealthCues to {cert.userName}.</div>
              </div>
            </div>
            <CertificateDocument cert={cert} verified={true} />
          </>
        )}

        <div style={{ marginTop: 28, fontSize: 11, color: C.textLight, textAlign: 'center' }}>
          © 2026 HealthCues. Certificate IDs are unique and tamper-evident.
        </div>
      </div>
    </div>
  );
}
